import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowLeft, FaFileContract, FaCheck, 
  FaTimes, FaInfoCircle, FaShieldAlt,
  FaUserCheck, FaBan, FaGavel, FaWallet,
  FaCoins, FaTicketAlt, FaTrophy
} from 'react-icons/fa';
import './LegalPage.css';

const Terms = () => {
  const lastUpdated = 'September 22, 2026';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">
          <FaArrowLeft /> Back to Home
        </Link>

        {/* Header */}
        <div className="legal-header">
          <div className="header-icon-wrap">
            <FaFileContract className="header-icon" />
          </div>
          <h1>Terms of Service</h1>
          <p className="legal-subtitle">
            Last updated: <strong>{lastUpdated}</strong>
          </p>
          <p className="legal-intro">
            Welcome to XPunters. By accessing or using our platform, you agree 
            to be bound by these Terms of Service. Please read them carefully 
            before using our services.
          </p>
        </div>

        {/* Quick Summary */}
        <div className="legal-summary">
          <h3><FaInfoCircle /> Quick Summary</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <FaUserCheck className="summary-icon green" />
              <div>
                <strong>You must be 18+</strong>
                <p>You must be of legal betting age in your jurisdiction</p>
              </div>
            </div>
            <div className="summary-item">
              <FaShieldAlt className="summary-icon blue" />
              <div>
                <strong>Play Fair</strong>
                <p>No manipulation, bots, or fraudulent activity</p>
              </div>
            </div>
            <div className="summary-item">
              <FaWallet className="summary-icon gold" />
              <div>
                <strong>Your Money is Yours</strong>
                <p>You can withdraw your balance anytime</p>
              </div>
            </div>
            <div className="summary-item">
              <FaBan className="summary-icon red" />
              <div>
                <strong>We Can Terminate Accounts</strong>
                <p>For violations of these terms</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="legal-content">
          <section className="legal-section">
            <h2><span className="section-number">1</span> Eligibility</h2>
            <p>To use XPunters, you must:</p>
            <ul>
              <li>Be at least 18 years old, or the legal betting age in your jurisdiction (whichever is higher)</li>
              <li>Not be prohibited by any applicable law from using our services</li>
              <li>Register with accurate and complete information</li>
              <li>Maintain only one account (multiple accounts will be closed)</li>
              <li>Not have been previously banned from using our platform</li>
            </ul>
            <p className="legal-highlight">
              <strong>Responsibility:</strong> You are solely responsible for 
              determining whether your use of XPunters is legal in your jurisdiction.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">2</span> Account Responsibilities</h2>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your password and login credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
              <li>Ensuring your account information is current and accurate</li>
              <li>Not sharing your account with any third party</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that we believe 
              have been compromised or misused.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">3</span> $XPUNT Currency</h2>
            <p>
              XPunters uses <strong>$XPUNT</strong> as its platform currency. 
              The following terms apply:
            </p>
            <ul>
              <li><strong>Value:</strong> 1 $XPUNT is pegged to 1 USD (United States Dollar)</li>
              <li><strong>Deposits:</strong> Fund your wallet via Flutterwave (local currency) or USDC crypto deposits</li>
              <li><strong>Withdrawals:</strong> Withdraw to your local bank account in your registered currency</li>
              <li><strong>Exchange Rate:</strong> Live exchange rates are applied at time of deposit/withdrawal</li>
              <li><strong>Crypto Deposits:</strong> 1 USDC = 1 $XPUNT with no conversion fees</li>
            </ul>
            <p className="legal-highlight">
              <strong>Note:</strong> $XPUNT is a platform currency and is not 
              a cryptocurrency or investment instrument. It cannot be transferred 
              between users outside of platform features.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">4</span> Bet Codes & Predictions</h2>
            <p>When using our prediction features:</p>
            <ul>
              <li>All bet codes and predictions are shared for informational purposes</li>
              <li>You are solely responsible for your own betting decisions</li>
              <li>Past performance does not guarantee future results</li>
              <li>We do not guarantee the accuracy of any bet code or prediction</li>
              <li>Creators are responsible for the content they post</li>
              <li>XPunters is not liable for any losses incurred from using shared bet codes</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">5</span> Punter+ Subscription</h2>
            <p>Punter+ subscribers get access to premium features:</p>
            <ul>
              <li><strong>Monthly:</strong> 2 $XPUNT per month</li>
              <li><strong>Bi-Annual:</strong> 10 $XPUNT per 6 months (save 20%)</li>
              <li><strong>Yearly:</strong> 18 $XPUNT per year (save 33%)</li>
            </ul>
            <p>
              Subscriptions auto-renew unless cancelled. Cancellations take 
              effect at the end of the current billing period — no refunds 
              for partial periods.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">6</span> Creator Subscriptions</h2>
            <p>Punter+ users can become Creators and charge subscribers:</p>
            <ul>
              <li>Creators set their own subscription price in $XPUNT</li>
              <li>A <strong>15% admin fee</strong> (capped at 5 $XPUNT) is applied per subscription</li>
              <li>Subscribers gain access to the creator's private bet codes and rollover games</li>
              <li>If a creator's Punter+ subscription expires, new subscribers cannot join</li>
              <li>Existing subscribers retain access until their subscription expires</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">7</span> Referral Program</h2>
            <p>
              Our referral program rewards users with <strong>20% of admin fees</strong> 
              generated from referrals. This means:
            </p>
            <ul>
              <li>You earn when your referred users win predictions</li>
              <li>Earnings are credited automatically to your wallet</li>
              <li>There is no limit to referral earnings</li>
              <li>Self-referrals and fraudulent referrals will be voided</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">8</span> Prohibited Activities</h2>
            <p>You may NOT:</p>
            <ul>
              <li>Use the platform for money laundering or illegal activities</li>
              <li>Operate multiple accounts to exploit bonuses or referrals</li>
              <li>Use bots, scripts, or automated tools to interact with the platform</li>
              <li>Attempt to manipulate game results or other users</li>
              <li>Post abusive, defamatory, or illegal content</li>
              <li>Reverse engineer, hack, or attempt to breach platform security</li>
              <li>Impersonate other users, staff, or administrators</li>
            </ul>
            <p className="legal-highlight red">
              <strong>Violations:</strong> Accounts found violating these rules 
              will be suspended, funds may be frozen, and legal action may follow.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">9</span> Deposits & Withdrawals</h2>
            <ul>
              <li><strong>Minimum withdrawal:</strong> 10 $XPUNT</li>
              <li>Withdrawals are processed within 24–72 hours</li>
              <li>Crypto deposits require admin approval and cannot be reversed</li>
              <li>Deposit transaction IDs must be unique and valid</li>
              <li>We reserve the right to verify the source of deposits</li>
              <li>Fraudulent deposits will result in account termination</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">10</span> Limitation of Liability</h2>
            <p>
              XPunters is provided "as is" without warranty. To the maximum 
              extent permitted by law:
            </p>
            <ul>
              <li>We are not liable for any indirect, incidental, or consequential damages</li>
              <li>We are not responsible for third-party services (Flutterwave, payment processors)</li>
              <li>We do not guarantee continuous, uninterrupted access to the platform</li>
              <li>Our total liability shall not exceed the fees you paid in the last 12 months</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">11</span> Modifications</h2>
            <p>
              We reserve the right to modify these Terms at any time. 
              Significant changes will be communicated via email or platform 
              notification. Continued use of XPunters after changes constitutes 
              acceptance of the new Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">12</span> Governing Law</h2>
            <p>
              These Terms are governed by the laws of the Federal Republic of 
              Nigeria. Any disputes will be resolved in the courts of competent 
              jurisdiction in Nigeria.
            </p>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">13</span> Contact</h2>
            <p>
              For questions about these Terms, contact us at:
            </p>
            <div className="contact-block">
              <p><strong>Email:</strong> support@xpunters.com</p>
              <p><strong>Response Time:</strong> Within 48 hours</p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="legal-footer">
          <p>
            By using XPunters, you acknowledge that you have read, understood, 
            and agree to be bound by these Terms of Service.
          </p>
          <div className="legal-footer-actions">
            <Link to="/privacy" className="btn btn-secondary">
              View Privacy Policy
            </Link>
            <Link to="/contact" className="btn btn-primary">
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;